import { useEffect, useState, Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './index.css'
import AppLayout from './components/AppLayout'
import CanvasPane from './components/CanvasPane'
import { Toaster } from './components/ui/toaster'
import { ErrorBoundary } from './components/ErrorBoundary'
import { TooltipProvider } from './components/ui/tooltip' // OPTIMIZATION: Global TooltipProvider
import { GlobalCommandPaletteProvider } from './components/GlobalCommandPalette' // PROJ-039: Global Command Palette
import { SessionProvider } from './components/session' // PROJ-035: Session Expiration Handling
// ============================================================================
// IMPORTS - Optimized with lazy loading for admin/config modules
// ============================================================================

// STATIC IMPORTS - Auth/public pages that must load immediately
import { PortalLanding } from './pages/PortalLanding'
import AcceptInvite from './pages/AcceptInvite'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AuthCallback from './pages/AuthCallback' // PROJ-025: OAuth callback handler
import FormPage from './pages/FormPage' // PROJ-054: Public workflow forms
import { usePreloadPermissions } from './hooks/usePermission'

// ============================================================================
// LAZY IMPORTS - Admin/Config modules loaded on demand
// Reduces initial bundle by ~2MB+ (these modules + their dependencies)
// ============================================================================

// Operational pages - Main business functionality (lazy for faster initial load)
const Incidents = lazy(() => import('./pages/Incidents'))
const IncidentDetail = lazy(() => import('./pages/IncidentDetail'))
const Persons = lazy(() => import('./pages/Persons'))
const Companies = lazy(() => import('./pages/Companies'))

// Organization structure
const Organization = lazy(() => import('./pages/Organization'))
const BusinessUnits = lazy(() => import('./pages/BusinessUnits'))
const Teams = lazy(() => import('./pages/Teams'))
const UsersAndCollaborators = lazy(() => import('./pages/UsersAndCollaborators'))

// Demo/utility
const AvatarDemo = lazy(() => import('./pages/AvatarDemo').then(m => ({ default: m.AvatarDemo })))

// Heavy modules with ReactFlow dependency (~1.6MB)
const VisualFlowBuilder = lazy(() => import('./pages/VisualFlowBuilder'))
const WorkflowBuilderPage = lazy(() => import('./pages/WorkflowBuilderPage'))
const WorkflowAutomation = lazy(() => import('./pages/WorkflowAutomation'))
const VisualFlows = lazy(() => import('./pages/VisualFlows'))
const AllExecutionsPage = lazy(() => import('./pages/AllExecutionsPage'))

// Email module with MJML (~1.9MB)
const EmailManagement = lazy(() => import('./pages/EmailManagement'))

// Developer/Admin tools (large, rarely used)
const MCPConsole = lazy(() => import('./pages/MCPConsole'))
const ApisFastApi = lazy(() => import('./pages/ApisFastApi'))
const Diagnostics = lazy(() => import('./pages/Diagnostics'))

// Security & Audit modules
const Security = lazy(() => import('./pages/Security'))
const MySessions = lazy(() => import('./pages/MySessions'))
const LoginHistory = lazy(() => import('./pages/LoginHistory'))
const SecurityAlerts = lazy(() => import('./pages/SecurityAlerts'))
const ComplianceReports = lazy(() => import('./pages/ComplianceReports'))

// Configuration modules
const Credentials = lazy(() => import('./pages/Credentials'))
const WebhookManagement = lazy(() => import('./pages/WebhookManagement'))
const SystemSettings = lazy(() => import('./pages/SystemSettings'))
const IconManagement = lazy(() => import('./pages/IconManagement'))
const FieldMetadata = lazy(() => import('./pages/FieldMetadata'))

// Document management
const Documents = lazy(() => import('./pages/Documents'))
const DocumentCategories = lazy(() => import('./pages/DocumentCategories'))

// Artifacts (PROJ-086: Files for Email Attachments)
const Artifacts = lazy(() => import('./pages/Artifacts'))

// Data management
const Tags = lazy(() => import('./pages/Tags'))
const Industries = lazy(() => import('./pages/Industries'))
const DataClassifications = lazy(() => import('./pages/DataClassifications'))
const DataAttributes = lazy(() => import('./pages/DataAttributes'))
const Compliance = lazy(() => import('./pages/Compliance'))
const Duplicates = lazy(() => import('./pages/admin/DataQuality/Duplicates'))
const MergeHistory = lazy(() => import('./pages/admin/MergeHistory'))

// Visualization modules
const Dashboard = lazy(() => import('./pages/Dashboard'))
const BusinessUnitGraph = lazy(() => import('./pages/BusinessUnitGraph'))
import * as userApi from './lib/userApi'
import * as auth from './lib/auth'
import { cleanupOnLogout } from './utils/cleanup'
export default function App() {
  const [bgVariant, setBgVariant] = useState<'none' | 'dots' | 'lines' | 'cross'>('dots')
  const [edgeKind] = useState<'default' | 'smoothstep' | 'step' | 'straight' | 'bezier'>('smoothstep')
  const [showGrid, setShowGrid] = useState(true)
  const [showDevtools, setShowDevtools] = useState(false)
  const [initials, setInitials] = useState('??')
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)
  const permissionsReady = usePreloadPermissions()

  useEffect(() => {
    async function checkAuthentication() {
      const token = auth.getAccessToken();
      if (token) {
        try {
          const user = await userApi.getCurrentUser();
          setIsAuthenticated(true);
          const email = user.email || 'guest@local';
          setInitials(email.split('@')[0].slice(0, 2).toUpperCase());
        } catch (error) {
          // Token invalid or expired
          setIsAuthenticated(false);
          setInitials('??');
        }
      } else {
        setIsAuthenticated(false);
      }
      setCheckingAuth(false);
    }

    checkAuthentication();
  }, []);

  const doLogout = async () => {
    try {
      await userApi.logout();
    } catch {
      // Continue logout even if backend call fails
    }
    await cleanupOnLogout();
    window.location.href = '/';
  };

  const handleLoginSuccess = () => {
    // Reload auth state after successful login
    setCheckingAuth(true);
    (async () => {
      try {
        const user = await userApi.getCurrentUser();
        setIsAuthenticated(true);
        const email = user.email || 'guest@local';
        setInitials(email.split('@')[0].slice(0, 2).toUpperCase());
      } catch {
        setIsAuthenticated(false);
        setInitials('??');
      } finally {
        setCheckingAuth(false);
      }
    })();
  };

  if (checkingAuth) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    )
  }

  if (isAuthenticated && !permissionsReady) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">Cargando permisos...</div>
      </div>
    )
  }

  const LoadingFallback = () => (
    <div className="flex h-screen items-center justify-center">
      <div className="text-muted-foreground">Cargando módulo...</div>
    </div>
  );

  // Show login page when not authenticated (after logout or first visit without auto-login)
  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <TooltipProvider>
          <GlobalCommandPaletteProvider>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/avatar-demo" element={<AvatarDemo />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                {/* PROJ-054: Public workflow forms (accessible without authentication) */}
                <Route path="/forms/test/:workflowId/:slug" element={<FormPage />} />
                <Route path="/forms/:workflowId/:slug" element={<FormPage />} />
                <Route path="*" element={<PortalLanding onLoginSuccess={handleLoginSuccess} />} />
              </Routes>
            </Suspense>
            <Toaster />
          </GlobalCommandPaletteProvider>
        </TooltipProvider>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      {/* OPTIMIZATION: Global TooltipProvider - no need for individual providers in components */}
      <TooltipProvider>
        <GlobalCommandPaletteProvider>
          {/* PROJ-035: SessionProvider handles proactive token refresh and expired session modal */}
          <SessionProvider>
            <Routes>
              {/* PROJ-054: Full-screen workflow builder (outside AppLayout for n8n/Zapier-like experience) */}
              <Route
                path="/workflow-automation/:id/builder"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <WorkflowBuilderPage />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              {/* PROJ-059: Full-screen visual flow builder */}
              <Route
                path="/visual-flows/:id/builder"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <VisualFlowBuilder />
                    </Suspense>
                  </ErrorBoundary>
                }
              />

              {/* PROJ-054: Public workflow forms (outside SessionProvider for unauthenticated access) */}
              <Route
                path="/forms/test/:workflowId/:slug"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <FormPage />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/forms/:workflowId/:slug"
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <FormPage />
                    </Suspense>
                  </ErrorBoundary>
                }
              />

              {/* Public-style pages accessible even when authenticated (full-screen, no sidebar) */}
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* All other routes with AppLayout (sidebar navigation) */}
              <Route path="*" element={
                <AppLayout
                  initials={initials}
                  onLogin={() => { }}
                  onLogout={doLogout}
                  bgVariant={bgVariant}
                  onBgVariantChange={setBgVariant}
                  showGrid={showGrid}
                  onShowGridChange={setShowGrid}
                  showDevtools={showDevtools}
                  onShowDevtoolsChange={setShowDevtools}
                >
                  <ErrorBoundary>
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route
                        path="/canvas"
                        element={
                          <CanvasPane
                            bgVariant={bgVariant}
                            edgeKind={edgeKind}
                            showGrid={showGrid}
                            showDevtools={showDevtools}
                          />
                        }
                      />
                      <Route path="/users" element={<UsersAndCollaborators />} />
                      <Route path="/usuarios-y-colaboradores" element={<UsersAndCollaborators />} />
                      <Route path="/organization" element={<Organization />} />
                      <Route path="/business-units" element={<BusinessUnits />} />
                      <Route path="/business-unit-graph" element={<BusinessUnitGraph />} />
                      <Route path="/teams" element={<Teams />} />
                      <Route path="/tags" element={<Tags />} />
                      <Route path="/industries" element={<Industries />} />
                      <Route path="/visual-flows" element={<VisualFlows />} />
                      <Route path="/workflow-automation" element={<WorkflowAutomation />} />
                      <Route path="/workflow-automation/executions" element={<AllExecutionsPage />} />
                      <Route path="/diagnostics" element={<Diagnostics />} />
                      {/* NetworkDiagnostics merged into Diagnostics - redirect for backwards compat */}
                      <Route path="/network-diagnostics" element={<Navigate to="/diagnostics" replace />} />
                      <Route path="/visual-catalog" element={<IconManagement />} />
                      {/* Redirect legacy /assets route to /visual-catalog */}
                      <Route path="/assets" element={<Navigate to="/visual-catalog" replace />} />
                      <Route path="/compliance" element={<Compliance />} />
                      <Route path="/data-classification" element={<DataClassifications />} />
                      <Route path="/data-attributes" element={<DataAttributes />} />
                      {/* Personas y Compañías - Módulos independientes */}
                      <Route path="/persons" element={<Persons />} />
                      <Route path="/companies" element={<Companies />} />
                      <Route path="/incidents" element={<Incidents />} />
                      <Route path="/incidents/:id" element={<IncidentDetail />} />
                      <Route path="/apis" element={<ApisFastApi />} />
                      <Route path="/system-settings" element={<SystemSettings />} />
                      <Route path="/security" element={<Security />} />
                      <Route path="/my-sessions" element={<MySessions />} />
                      <Route path="/login-history" element={<LoginHistory />} />
                      <Route path="/security-alerts" element={<SecurityAlerts />} />
                      <Route path="/compliance-reports" element={<ComplianceReports />} />
                      <Route path="/webhooks" element={<WebhookManagement />} />
                      <Route path="/credentials" element={<Credentials />} />
                      <Route path="/artifacts" element={<Artifacts />} />
                      <Route path="/mcp" element={<MCPConsole />} />
                      <Route path="/email" element={<EmailManagement />} />
                      <Route path="/field-metadata" element={<FieldMetadata />} />
                      <Route path="/documents" element={<Documents />} />
                      <Route path="/document-categories" element={<DocumentCategories />} />
                      <Route path="/admin/data-quality/duplicates" element={<Duplicates />} />
                      <Route path="/admin/merge-history" element={<MergeHistory />} />
                      </Routes>
                    </Suspense>
                  </ErrorBoundary>
                </AppLayout>
              } />
            </Routes>
            <Toaster />
          </SessionProvider>
        </GlobalCommandPaletteProvider>
      </TooltipProvider>
    </BrowserRouter>
  )
}
